import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { useUsers } from '../context/UsersContext';
import { usePermissions } from '../hooks/usePermissions';
import AdvancedFilterDrawer from '../components/AdvancedFilterDrawer';
import MonthYearPicker from '../components/MonthYearPicker';
import SearchableDropdown from '../components/SearchableDropdown';
import ColumnSelector from '../components/ColumnSelector';
import Tooltip from '../components/Tooltip';
import UserTimestamp from '../components/UserTimestamp';
import { Info, Copy, RotateCcw } from 'lucide-react';
import { formatDateUS, formatDateRange, getCurrentMonthYear, formatMonthYear, getMonthRange } from '../utils/dateUtils';
import { filterEmojis } from '../utils/emojiFilter';
import { loadVisibleColumns, persistVisibleColumns, sanitizeVisibleColumns } from '../utils/columnPreferences';
import { useResizableTableColumns } from '../hooks/useResizableTableColumns';
import { CellEllipsis } from '../components/CellEllipsis';
import { ResizableTh } from '../components/ResizableTableHeader';
import './DitDrl.css';

const DITDRL_COLUMN_PREFS_KEY = 'ditDrlTable';
const DITDRL_TABLE_WIDTHS_KEY = 'ditDrlTableColWidths';
const DITDRL_DEFAULT_COL_WIDTH_PX = {
  drlReceivedDate: 108,
  drlNumber: 124,
  totalDrlAmount: 112,
  totalPostedAmount: 112,
  remainingAmount: 108,
  checksAmount: 132,
  reconciliationStatus: 130,
  status: 104,
  updatedAt: 118,
  practiceCode: 104,
  locationCode: 104,
  createdAt: 118,
  reconciliationDate: 118,
  completedDate: 124,
  offshorePostingAmount: 112,
  otherAmount: 104,
  onshorePostingAmount: 112,
  assigneeName: 120,
  reporterName: 120,
  action: 56
};
const DITDRL_LOAD_KEYS = new Set([
  'drlReceivedDate',
  'drlNumber',
  'totalDrlAmount',
  'totalPostedAmount',
  'remainingAmount',
  'checksAmount',
  'reconciliationStatus',
  'status',
  'updatedAt',
  'practiceCode',
  'locationCode',
  'createdAt',
  'reconciliationDate',
  'completedDate',
  'offshorePostingAmount',
  'otherAmount',
  'onshorePostingAmount',
  'assigneeName',
  'reporterName',
  'action'
]);
const DITDRL_LOAD_FALLBACK_DEFAULTS = [
  'drlReceivedDate',
  'drlNumber',
  'totalDrlAmount',
  'totalPostedAmount',
  'remainingAmount',
  'checksAmount',
  'status',
  'updatedAt'
];

const DitDrl = () => {
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Storage key for persisting filters
  const STORAGE_KEY = 'ditDrlFiltersState';

  // Helper function to load filters from sessionStorage (called once during initialization)
  const loadInitialFilters = () => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading filters from sessionStorage:', error);
    }
    return null;
  };

  const initialFilters = loadInitialFilters();
  
  // State management
  const [ditDrls, setDitDrls] = useState([]);
  const [allDitDrls, setAllDitDrls] = useState([]); // Store all fetched DIT/DRL Payments before filtering
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination - initialize from storage if available
  const [currentPage, setCurrentPage] = useState(initialFilters?.currentPage ?? 0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageInputValue, setPageInputValue] = useState('');
  
  // Default filters - initialize from storage if available
  const [searchTerm, setSearchTerm] = useState(initialFilters?.searchTerm ?? '');
  const [selectedStatus, setSelectedStatus] = useState(initialFilters?.selectedStatus ?? '');
  const [selectedPractice, setSelectedPractice] = useState(initialFilters?.selectedPractice ?? '');
  const [practices, setPractices] = useState([]);
  
  // Advanced filters - now support arrays for multiple selections
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const defaultAdvancedFilters = {
    statuses: [], // Array for multiple status selections
    practiceCodes: [], // Array for multiple practice code selections
    locationCodes: [], // Array for multiple location code selections
    assigneeIds: [], // Array for multiple assignee selections
    excludeAssigneeIds: [],
    reporteeIds: [], // Array for multiple reporter selections
    excludeReporteeIds: [],
    assigneeTypes: [], // Array for multiple assignee type selections (ONSHORE, OFFSHORE)
    reporteeTypes: [], // Array for multiple reportee type selections (ONSHORE, OFFSHORE)
    reconciliationStatuses: [], // Array for multiple reconciliation status selections
    drlNumber: '',
    batchNumber: '',
    drlReceivedFrom: '',
    drlReceivedTo: '',
    reconciliationDateFrom: '',
    reconciliationDateTo: '',
    completedDateFrom: '',
    completedDateTo: '',
    createdAtFrom: '',
    createdAtTo: '',
    month: null,
    year: null
  };
  const [advancedFilters, setAdvancedFilters] = useState(() => {
    const filters = initialFilters?.advancedFilters ?? defaultAdvancedFilters;
    // Ensure assigneeIds, reporteeIds, assigneeTypes, and reporteeTypes are always arrays
    return {
      ...defaultAdvancedFilters,
      ...filters,
      assigneeIds: Array.isArray(filters.assigneeIds) ? filters.assigneeIds : [],
      reporteeIds: Array.isArray(filters.reporteeIds) ? filters.reporteeIds : [],
      excludeAssigneeIds: Array.isArray(filters.excludeAssigneeIds) ? filters.excludeAssigneeIds : [],
      excludeReporteeIds: Array.isArray(filters.excludeReporteeIds) ? filters.excludeReporteeIds : [],
      assigneeTypes: Array.isArray(filters.assigneeTypes) ? filters.assigneeTypes : [],
      reporteeTypes: Array.isArray(filters.reporteeTypes) ? filters.reporteeTypes : []
    };
  });
  
  // Sorting - initialize from storage if available
  const [sortField, setSortField] = useState(initialFilters?.sortField ?? 'drlReceivedDate');
  const [sortDirection, setSortDirection] = useState(initialFilters?.sortDirection ?? 'asc'); // Default: oldest first (by Received Date)
  
  const { getUserName, getUserById } = useUsers(); // Get users from context
  const { isSuperAdmin } = usePermissions(); // Get super admin status

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Column selector
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const columnSelectorTriggerRef = useRef(null);

  // Hide pagination during filter changes to prevent flickering
  const [showPagination, setShowPagination] = useState(true);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [drlToDelete, setDrlToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Available columns - defined as constant array
  const allColumns = useMemo(
    () => [
      { key: 'drlReceivedDate', label: 'Received date' },
      { key: 'drlNumber', label: 'DRL number' },
      { key: 'totalDrlAmount', label: 'Total drl $' },
      { key: 'totalPostedAmount', label: 'Total posted $' },
      { key: 'remainingAmount', label: 'Remaining $' },
      { key: 'checksAmount', label: 'Total received in bank $' },
      { key: 'reconciliationStatus', label: 'Reconciliation status' },
      { key: 'status', label: 'Status' },
      { key: 'updatedAt', label: 'Updated at' },
      { key: 'practiceCode', label: 'Practice code' },
      { key: 'locationCode', label: 'Location code' },
      { key: 'createdAt', label: 'Created at' },
      { key: 'reconciliationDate', label: 'Reconciliation date' },
      { key: 'completedDate', label: 'Posting completed date' },
      { key: 'offshorePostingAmount', label: 'offShore Posting' },
      { key: 'otherAmount', label: 'Other Amount' },
      { key: 'onshorePostingAmount', label: 'onShore Posting' },
      { key: 'assigneeName', label: 'Assignee Name' },
      { key: 'reporterName', label: 'Reporter Name' },
      ...(isSuperAdmin ? [{ key: 'action', label: 'Action', alwaysVisible: true }] : [])
    ],
    [isSuperAdmin]
  );

  const defaultColumns = useMemo(
    () => [
      'drlReceivedDate',
      'drlNumber',
      'totalDrlAmount',
      'totalPostedAmount',
      'remainingAmount',
      'checksAmount',
      'status',
      'updatedAt',
      ...(isSuperAdmin ? ['action'] : [])
    ],
    [isSuperAdmin]
  );

  const [visibleColumns, setVisibleColumns] = useState(() =>
    loadVisibleColumns(DITDRL_COLUMN_PREFS_KEY, DITDRL_LOAD_KEYS, DITDRL_LOAD_FALLBACK_DEFAULTS)
  );

  useEffect(() => {
    const allowed = new Set(allColumns.map(c => c.key));
    setVisibleColumns(prev => {
      let next = sanitizeVisibleColumns(prev, allowed, defaultColumns, 10);
      if (isSuperAdmin && !next.includes('action')) {
        next = [...next, 'action'];
      }
      if (!isSuperAdmin) {
        next = next.filter(col => col !== 'action');
      }
      return next;
    });
  }, [isSuperAdmin, allColumns, defaultColumns]);

  const ditDrlColumnOrder = useMemo(() => [...visibleColumns], [visibleColumns]);
  const { Colgroup, onResizeMouseDown, resetColumnWidths } = useResizableTableColumns({
    storageKey: DITDRL_TABLE_WIDTHS_KEY,
    columnIds: ditDrlColumnOrder,
    defaultWidths: DITDRL_DEFAULT_COL_WIDTH_PX,
    minWidth: 48
  });

  // Month/Year selection - initialize from storage or defaults
  const currentMonthYear = getCurrentMonthYear();
  const [selectedMonth, setSelectedMonth] = useState(
    initialFilters?.selectedMonth ?? currentMonthYear.month
  );
  const [selectedYear, setSelectedYear] = useState(
    initialFilters?.selectedYear ?? currentMonthYear.year
  );
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  const handleMonthYearSelect = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    setShowMonthPicker(false);
  };

  // Function to save filters to sessionStorage
  const saveFiltersToStorage = useCallback(() => {
    try {
      const filterState = {
        searchTerm,
        selectedStatus,
        selectedPractice,
        advancedFilters,
        selectedMonth,
        selectedYear,
        sortField,
        sortDirection,
        currentPage
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filterState));
    } catch (error) {
      console.error('Error saving filters to sessionStorage:', error);
    }
  }, [searchTerm, selectedStatus, selectedPractice, advancedFilters, selectedMonth, selectedYear, sortField, sortDirection, currentPage]);

  const goToDrlDetail = useCallback(
    (drl) => {
      if (!drl?.drlId) return;
      const drlIds = ditDrls.map((d) => d.drlId);
      const filters = {
        status: selectedStatus || '',
        practiceCode: selectedPractice || '',
        assigneeId: advancedFilters.assigneeIds?.[0] || '',
        reporteeId: advancedFilters.reporteeIds?.[0] || '',
        drlReceivedFrom: advancedFilters.drlReceivedFrom || '',
        drlReceivedTo: advancedFilters.drlReceivedTo || '',
        month: advancedFilters.month || selectedMonth,
        year: advancedFilters.year || selectedYear,
        page: currentPage,
        size: 50
      };
      const completeFilterState = {
        searchTerm,
        selectedStatus,
        selectedPractice,
        advancedFilters,
        selectedMonth,
        selectedYear,
        sortField,
        sortDirection,
        currentPage
      };
      sessionStorage.setItem('drlNavigationIds', JSON.stringify(drlIds));
      sessionStorage.setItem('drlNavigationFilters', JSON.stringify(filters));
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(completeFilterState));
      saveFiltersToStorage();
      navigate(`/dit-drl/${drl.drlId}`);
    },
    [
      ditDrls,
      selectedStatus,
      selectedPractice,
      advancedFilters,
      selectedMonth,
      selectedYear,
      currentPage,
      searchTerm,
      sortField,
      sortDirection,
      navigate,
      saveFiltersToStorage
    ]
  );

  const handleCopyDrlNumber = useCallback(
    async (drlNumber) => {
      if (!drlNumber) return;
      try {
        await navigator.clipboard.writeText(drlNumber);
        showSuccess('DRL number copied');
      } catch (err) {
        console.error('Copy failed:', err);
        showError('Could not copy DRL number');
      }
    },
    [showSuccess, showError]
  );

  // Save filters to sessionStorage whenever they change (skip initial mount to avoid overwriting)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip saving on initial mount
    }
    saveFiltersToStorage();
  }, [saveFiltersToStorage]);

  // Restore filters when navigating back to /dit-drl page
  const hasRestoredFromStorage = useRef(false);
  const isRestoringFromStorage = useRef(false);
  useEffect(() => {
    if (location.pathname === '/dit-drl' && !hasRestoredFromStorage.current) {
      const stored = loadInitialFilters();
      if (stored) {
        isRestoringFromStorage.current = true;
        // Keep pagination visible when restoring from storage
        setShowPagination(true);
        // Restore page first (before filters) to prevent reset effect from interfering
        if (stored.currentPage !== undefined && stored.currentPage !== currentPage) {
          setCurrentPage(stored.currentPage);
        }
        // Restore filters - the reset effect will be skipped due to isRestoringFromStorage flag
        if (stored.searchTerm !== searchTerm) setSearchTerm(stored.searchTerm || '');
        if (stored.selectedStatus !== selectedStatus) setSelectedStatus(stored.selectedStatus || '');
        if (stored.selectedPractice !== selectedPractice) setSelectedPractice(stored.selectedPractice || '');
        if (JSON.stringify(stored.advancedFilters) !== JSON.stringify(advancedFilters)) {
          const restoredFilters = stored.advancedFilters || defaultAdvancedFilters;
          // Ensure assigneeIds, reporteeIds, assigneeTypes, and reporteeTypes are always arrays
          setAdvancedFilters({
            ...defaultAdvancedFilters,
            ...restoredFilters,
            assigneeIds: Array.isArray(restoredFilters.assigneeIds) ? restoredFilters.assigneeIds : [],
            reporteeIds: Array.isArray(restoredFilters.reporteeIds) ? restoredFilters.reporteeIds : [],
            excludeAssigneeIds: Array.isArray(restoredFilters.excludeAssigneeIds) ? restoredFilters.excludeAssigneeIds : [],
            excludeReporteeIds: Array.isArray(restoredFilters.excludeReporteeIds) ? restoredFilters.excludeReporteeIds : [],
            assigneeTypes: Array.isArray(restoredFilters.assigneeTypes) ? restoredFilters.assigneeTypes : [],
            reporteeTypes: Array.isArray(restoredFilters.reporteeTypes) ? restoredFilters.reporteeTypes : []
          });
        }
        if (stored.selectedMonth !== selectedMonth) setSelectedMonth(stored.selectedMonth ?? currentMonthYear.month);
        if (stored.selectedYear !== selectedYear) setSelectedYear(stored.selectedYear ?? currentMonthYear.year);
        if (stored.sortField !== sortField) setSortField(stored.sortField || 'drlReceivedDate');
        if (stored.sortDirection !== sortDirection) setSortDirection(stored.sortDirection || 'asc');
        // Clear the flag after all restorations are queued
        setTimeout(() => {
          isRestoringFromStorage.current = false;
        }, 0);
      }
      hasRestoredFromStorage.current = true;
    }
    // Reset the flag when navigating away
    if (location.pathname !== '/dit-drl') {
      hasRestoredFromStorage.current = false;
      isRestoringFromStorage.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Fetch practices on mount
  useEffect(() => {
    fetchPractices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPractices = async () => {
    try {
      const response = await api.getAllPractices();
      const practicesList = Array.isArray(response) ? response : (response?.items || []);
      setPractices(practicesList.filter(p => p.isActive !== false));
    } catch (error) {
      console.error('Error fetching practices:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to fetch practices';
      showError(errorMessage);
    }
  };

  const fetchDitDrls = useCallback(async (page = 0) => {
    // Set loading state but keep old data visible to prevent flickering
    setLoading(true);
    setError(null);
    
    try {
      // Build search parameters for POST request
      const searchParams = {
        page: page,
        size: 50
      };

      // Collect statuses from both default and advanced filters
      const statuses = [];
      if (selectedStatus) {
        statuses.push(selectedStatus);
      }
      if (advancedFilters.statuses && advancedFilters.statuses.length > 0) {
        statuses.push(...advancedFilters.statuses);
      }
      // Remove duplicates
      if (statuses.length > 0) {
        searchParams.statuses = [...new Set(statuses)];
      }

      // Collect practice codes
      const practiceCodesList = [];
      if (selectedPractice) {
        practiceCodesList.push(selectedPractice);
      }
      if (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) {
        practiceCodesList.push(...advancedFilters.practiceCodes);
      }
      // Remove duplicates
      if (practiceCodesList.length > 0) {
        searchParams.practiceCodes = [...new Set(practiceCodesList)];
      }

      // Location codes (array)
      if (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) {
        searchParams.locationCodes = [...new Set(advancedFilters.locationCodes)];
      }

      // Assignee IDs (array)
      if (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) {
        searchParams.assigneeIds = [...new Set(advancedFilters.assigneeIds)];
      }
      if (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) {
        searchParams.excludeAssigneeIds = [...new Set(advancedFilters.excludeAssigneeIds)];
      }

      // Reportee IDs (array) - Note: DIT/DRL uses reporteeIds, Checks uses reporterIds
      if (advancedFilters.reporteeIds && advancedFilters.reporteeIds.length > 0) {
        searchParams.reporteeIds = [...new Set(advancedFilters.reporteeIds)];
      }
      if (advancedFilters.excludeReporteeIds && advancedFilters.excludeReporteeIds.length > 0) {
        searchParams.excludeReporteeIds = [...new Set(advancedFilters.excludeReporteeIds)];
      }

      // Assignee Types (array) - Transform ONSHORE/OFFSHORE to ON-SHORE/OFF-SHORE for API
      if (advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0) {
        searchParams.assigneeTypes = [...new Set(advancedFilters.assigneeTypes)].map(type => {
          if (type === 'ONSHORE') return 'ON-SHORE';
          if (type === 'OFFSHORE') return 'OFF-SHORE';
          return type;
        });
      }

      // Reportee Types (array) - Transform ONSHORE/OFFSHORE to ON-SHORE/OFF-SHORE for API
      if (advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0) {
        searchParams.reporteeTypes = [...new Set(advancedFilters.reporteeTypes)].map(type => {
          if (type === 'ONSHORE') return 'ON-SHORE';
          if (type === 'OFFSHORE') return 'OFF-SHORE';
          return type;
        });
      }

      // DRL number (supports wildcards)
      if (advancedFilters.drlNumber && advancedFilters.drlNumber.trim() !== '') {
        searchParams.drlNumber = advancedFilters.drlNumber.trim();
      }

      // Batch number (supports wildcards)
      if (advancedFilters.batchNumber && advancedFilters.batchNumber.trim() !== '') {
        searchParams.batchNumber = advancedFilters.batchNumber.trim();
      }

      // Reconciliation statuses (array)
      if (advancedFilters.reconciliationStatuses && advancedFilters.reconciliationStatuses.length > 0) {
        searchParams.reconciliationStatuses = [...new Set(advancedFilters.reconciliationStatuses)];
      }

      // Date filters - map to DRL field names
      // Check if user has set any date filters
      const hasDrlReceivedDateFilter = advancedFilters.drlReceivedFrom || advancedFilters.drlReceivedTo;
      const hasReconciliationDateFilter = advancedFilters.reconciliationDateFrom || advancedFilters.reconciliationDateTo;
      const hasCompletedDateFilter = advancedFilters.completedDateFrom || advancedFilters.completedDateTo;
      const hasCreatedAtFilter = advancedFilters.createdAtFrom || advancedFilters.createdAtTo;
      
      // Check if any non-date advanced filters are active
      const hasNonDateAdvancedFilters = (
        (advancedFilters.statuses && advancedFilters.statuses.length > 0) ||
        (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) ||
        (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) ||
        (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) ||
        (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) ||
        (advancedFilters.reporteeIds && advancedFilters.reporteeIds.length > 0) ||
        (advancedFilters.excludeReporteeIds && advancedFilters.excludeReporteeIds.length > 0) ||
        (advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0) ||
        (advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0) ||
        (advancedFilters.reconciliationStatuses && advancedFilters.reconciliationStatuses.length > 0) ||
        (advancedFilters.drlNumber && advancedFilters.drlNumber.trim() !== '') ||
        (advancedFilters.batchNumber && advancedFilters.batchNumber.trim() !== '')
      );
      
      // Get month range for default date received
      const monthRange = getMonthRange(selectedMonth, selectedYear);
      
      // Only add date received filter if:
      // 1. User explicitly set DRL received date filters (use those instead of month range), OR
      // 2. Use default month range ONLY when no advanced filters are active
      if (hasDrlReceivedDateFilter) {
        // User has explicitly set a custom date range in advanced filters
        if (advancedFilters.drlReceivedFrom) {
          searchParams.drlReceivedFrom = advancedFilters.drlReceivedFrom;
        }
        if (advancedFilters.drlReceivedTo) {
          searchParams.drlReceivedTo = advancedFilters.drlReceivedTo;
        }
      } else if (!hasReconciliationDateFilter && !hasCompletedDateFilter && !hasCreatedAtFilter && !hasNonDateAdvancedFilters) {
        // No custom date filters or non-date filters set - use default month range
        searchParams.drlReceivedFrom = monthRange.startDate;
        searchParams.drlReceivedTo = monthRange.endDate;
      }
      
      // Reconciliation date filters
      if (advancedFilters.reconciliationDateFrom) {
        searchParams.reconciliationDateFrom = advancedFilters.reconciliationDateFrom;
      }
      if (advancedFilters.reconciliationDateTo) {
        searchParams.reconciliationDateTo = advancedFilters.reconciliationDateTo;
      }
      
      // Completed date filters
      if (advancedFilters.completedDateFrom) {
        searchParams.completedDateFrom = advancedFilters.completedDateFrom;
      }
      if (advancedFilters.completedDateTo) {
        searchParams.completedDateTo = advancedFilters.completedDateTo;
      }
      
      // Created at filters
      if (advancedFilters.createdAtFrom) {
        searchParams.createdAtFrom = advancedFilters.createdAtFrom;
      }
      if (advancedFilters.createdAtTo) {
        searchParams.createdAtTo = advancedFilters.createdAtTo;
      }

      // Use POST endpoint with search
      const response = await api.searchDrlDashboard(searchParams);
      
      let drlsList = response.items || [];
      const totalElements = response.totalElements || 0;
      
      // Calculate total posted amount (onshore + offshore)
      drlsList = drlsList.map(drl => ({
        ...drl,
        totalPostedAmount: (drl.onshorePostingAmount || 0) + (drl.offshorePostingAmount || 0),
        assigneeName: drl.assigneeId ? getUserName(drl.assigneeId) : '',
        reporterName: drl.reporteeId ? getUserName(drl.reporteeId) : ''
      }));
      
      // Client-side sorting (if backend doesn't support it)
      if (sortField && drlsList.length > 0) {
        drlsList = [...drlsList].sort((a, b) => {
          let aVal = a[sortField];
          let bVal = b[sortField];
          
          if (sortField === 'drlReceivedDate' || sortField === 'reconciliationDate' || sortField === 'completedDate') {
            aVal = aVal ? new Date(aVal) : new Date(0);
            bVal = bVal ? new Date(bVal) : new Date(0);
          } else if (sortField === 'createdAt' || sortField === 'updatedAt') {
            aVal = aVal ? new Date(aVal) : new Date(0);
            bVal = bVal ? new Date(bVal) : new Date(0);
          } else if (sortField === 'totalDrlAmount' || sortField === 'totalPostedAmount' || 
                     sortField === 'onshorePostingAmount' || sortField === 'offshorePostingAmount' || 
                     sortField === 'otherAmount' || sortField === 'checksAmount' ||
                     sortField === 'remainingAmount') {
            aVal = aVal || 0;
            bVal = bVal || 0;
          } else {
            aVal = aVal || '';
            bVal = bVal || '';
          }
          
          if (sortDirection === 'asc') {
            return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          } else {
            return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
          }
        });
      }
      
      // Update all state at once and show pagination after data loads
      setAllDitDrls(drlsList);
      setTotalElements(totalElements);
      setTotalPages(response.totalPages || 0);
      setHasMore(page < (response.totalPages || 0) - 1);
      setCurrentPage(page);
      setPageInputValue((page + 1).toString());
      setLoading(false);
      // Show pagination after data is loaded
      setShowPagination(true);
      
    } catch (err) {
      console.error('Error fetching DRLs:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to load DRLs. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
      setLoading(false);
    }
  }, [selectedStatus, selectedPractice, advancedFilters, sortField, sortDirection, selectedMonth, selectedYear, showError, getUserName]);

  // Track previous filter values to detect changes
  const prevFiltersRef = useRef({
    selectedStatus,
    selectedPractice,
    advancedFilters: JSON.stringify(advancedFilters),
    selectedMonth,
    selectedYear
  });

  // Fetch DRLs when page or filters change (excluding searchTerm - handled client-side)
  // Reset to page 0 when filters change, but not when only page changes
  useEffect(() => {
    const filtersChanged = 
      prevFiltersRef.current.selectedStatus !== selectedStatus ||
      prevFiltersRef.current.selectedPractice !== selectedPractice ||
      prevFiltersRef.current.advancedFilters !== JSON.stringify(advancedFilters) ||
      prevFiltersRef.current.selectedMonth !== selectedMonth ||
      prevFiltersRef.current.selectedYear !== selectedYear;

    if (filtersChanged && !isRestoringFromStorage.current) {
      // Hide pagination immediately when filters change to prevent flickering
      setShowPagination(false);
      // Reset to page 0 when filters change
      if (currentPage !== 0) {
        setCurrentPage(0);
        setPageInputValue('1');
        // Update ref for next comparison
        prevFiltersRef.current = {
          selectedStatus,
          selectedPractice,
          advancedFilters: JSON.stringify(advancedFilters),
          selectedMonth,
          selectedYear
        };
        return; // Don't fetch yet, let the page reset trigger the fetch
      }
    }

    // Update ref for next comparison
    prevFiltersRef.current = {
      selectedStatus,
      selectedPractice,
      advancedFilters: JSON.stringify(advancedFilters),
      selectedMonth,
      selectedYear
    };

    // Fetch data
    fetchDitDrls(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedStatus, selectedPractice, advancedFilters, sortField, sortDirection, selectedMonth, selectedYear]);


  const handlePreviousPage = () => {
    if (currentPage > 0 && !loading) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (hasMore && !loading) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setPageInputValue(value);
    }
  };

  const handlePageInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  const handleGoToPage = () => {
    if (pageInputValue === '' || loading) return;

    const pageNum = parseInt(pageInputValue, 10);
    const maxPage = totalPages > 0 ? totalPages - 1 : 0;

    // Validate page number (convert to 0-based index)
    if (pageNum >= 1 && pageNum <= maxPage + 1) {
      setCurrentPage(pageNum - 1);
    } else {
      // Reset to current page if invalid
      setPageInputValue((currentPage + 1).toString());
    }
  };

  // Update page input when currentPage changes externally
  useEffect(() => {
    setPageInputValue((currentPage + 1).toString());
  }, [currentPage]);

  // Calculate pagination display values
  const pageSize = 50;
  // If search is active, show filtered results count, otherwise show total from API
  const displayTotal = searchTerm ? ditDrls.length : totalElements;
  const startIndex = searchTerm ? 1 : (currentPage * pageSize + 1);
  const endIndex = searchTerm ? ditDrls.length : Math.min((currentPage + 1) * pageSize, totalElements);

  // Client-side filtering for search term (DRL number, amount)
  useEffect(() => {
    if (!searchTerm) {
      setDitDrls(allDitDrls);
      return;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const filtered = allDitDrls.filter(drl => {
      // Search by DRL number
      if (drl.drlNumber && drl.drlNumber.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search by amount (totalDrlAmount, totalPostedAmount, remainingAmount, checksAmount)
      const totalDrlAmount = String(drl.totalDrlAmount || '');
      const totalPostedAmount = String(drl.totalPostedAmount || '');
      const remainingAmount = String(drl.remainingAmount || '');
      const checksAmount = String(drl.checksAmount || '');
      
      if (totalDrlAmount.includes(searchLower) || 
          totalPostedAmount.includes(searchLower) || 
          remainingAmount.includes(searchLower) ||
          checksAmount.includes(searchLower)) {
        return true;
      }
      
      return false;
    });

    setDitDrls(filtered);
  }, [searchTerm, allDitDrls]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleApplyAdvancedFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const handleResetAdvancedFilters = () => {
    setAdvancedFilters({
      statuses: [],
      practiceCodes: [],
      locationCodes: [],
      assigneeIds: [],
      excludeAssigneeIds: [],
      reporteeIds: [],
      excludeReporteeIds: [],
      assigneeTypes: [],
      reporteeTypes: [],
      reconciliationStatuses: [],
      drlNumber: '',
      batchNumber: '',
      drlReceivedFrom: '',
      drlReceivedTo: '',
      reconciliationDateFrom: '',
      reconciliationDateTo: '',
      completedDateFrom: '',
      completedDateTo: '',
      createdAtFrom: '',
      createdAtTo: '',
      month: null,
      year: null
    });
  };

  const handleClearAllFilters = () => {
    // Reset advanced filters
    handleResetAdvancedFilters();
    // Reset basic filters
    setSelectedStatus('');
    setSelectedPractice('');
    setSearchTerm('');
    // Close advanced filter drawer
    setShowAdvancedFilters(false);
    // Clear from storage
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'COMPLETED': 'status-complete',
      'UNDER_CLARIFICATIONS': 'status-clarification',
      'IN_PROGRESS': 'status-progress',
      'NOT_STARTED': 'status-not-started',
      'OVER_POSTED': 'status-over-posted'
    };
    return statusMap[status] || 'status-not-started';
  };

  const formatStatus = (status) => {
    const statusMap = {
      'COMPLETED': 'Completed',
      'UNDER_CLARIFICATIONS': 'Clarifications',
      'IN_PROGRESS': 'In Progress',
      'NOT_STARTED': 'Not Started',
      'OVER_POSTED': 'Over Posted'
    };
    return statusMap[status] || status;
  };

  const getReconciliationStatusClass = (status) => {
    const statusMap = {
      'RECONCILED': 'status-complete',
      'OVER_RECONCILED': 'status-over-posted',
      'UNRECONCILED': 'status-not-started'
    };
    return statusMap[status] || 'status-not-started';
  };

  const formatReconciliationStatus = (status) => {
    const statusMap = {
      'RECONCILED': 'Reconciled',
      'OVER_RECONCILED': 'Excess Reconciled',
      'UNRECONCILED': 'Pending Reconciliation'
    };
    return statusMap[status] || status;
  };

  const handleDeleteClick = (drl) => {
    setDrlToDelete(drl);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!drlToDelete) return;
    
    setIsDeleting(true);
    try {
      await api.deleteDrl(drlToDelete.drlId);
      showSuccess(`DRL "${drlToDelete.drlNumber || 'Unknown'}" deleted successfully`);
      // Refresh DRLs list
      await fetchDitDrls(currentPage);
      setShowDeleteConfirm(false);
      setDrlToDelete(null);
    } catch (error) {
      console.error('Error deleting DRL:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to delete DRL. Please try again.';
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDrlToDelete(null);
  };

  // Check if any advanced filter is active
  const hasAdvancedFilters = () => {
    return (
      (advancedFilters.statuses && advancedFilters.statuses.length > 0) ||
      (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) ||
      (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) ||
      (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) ||
      (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) ||
      (advancedFilters.reporteeIds && advancedFilters.reporteeIds.length > 0) ||
      (advancedFilters.excludeReporteeIds && advancedFilters.excludeReporteeIds.length > 0) ||
      (advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0) ||
      (advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0) ||
      (advancedFilters.reconciliationStatuses && advancedFilters.reconciliationStatuses.length > 0) ||
      (advancedFilters.drlNumber && advancedFilters.drlNumber.trim() !== '') ||
      (advancedFilters.batchNumber && advancedFilters.batchNumber.trim() !== '') ||
      (advancedFilters.drlReceivedFrom && advancedFilters.drlReceivedFrom.trim() !== '') ||
      (advancedFilters.drlReceivedTo && advancedFilters.drlReceivedTo.trim() !== '') ||
      (advancedFilters.reconciliationDateFrom && advancedFilters.reconciliationDateFrom.trim() !== '') ||
      (advancedFilters.reconciliationDateTo && advancedFilters.reconciliationDateTo.trim() !== '') ||
      (advancedFilters.completedDateFrom && advancedFilters.completedDateFrom.trim() !== '') ||
      (advancedFilters.completedDateTo && advancedFilters.completedDateTo.trim() !== '') ||
      (advancedFilters.createdAtFrom && advancedFilters.createdAtFrom.trim() !== '') ||
      (advancedFilters.createdAtTo && advancedFilters.createdAtTo.trim() !== '') ||
      advancedFilters.month ||
      advancedFilters.year
    );
  };

  const getDateRangeDisplay = () => {
    // If DRL received date range is selected, show it
    if (advancedFilters.drlReceivedFrom && advancedFilters.drlReceivedTo) {
      return formatDateRange(advancedFilters.drlReceivedFrom, advancedFilters.drlReceivedTo);
    }
    // If reconciliation date range is selected, show it
    if (advancedFilters.reconciliationDateFrom && advancedFilters.reconciliationDateTo) {
      return formatDateRange(advancedFilters.reconciliationDateFrom, advancedFilters.reconciliationDateTo);
    }
    // If completed date range is selected, show it
    if (advancedFilters.completedDateFrom && advancedFilters.completedDateTo) {
      return formatDateRange(advancedFilters.completedDateFrom, advancedFilters.completedDateTo);
    }
    // If created at range is selected, show it
    if (advancedFilters.createdAtFrom && advancedFilters.createdAtTo) {
      return formatDateRange(advancedFilters.createdAtFrom, advancedFilters.createdAtTo);
    }
    // If advanced filters are active (but no date range), show custom range
    if (hasAdvancedFilters()) {
      return 'Custom Range';
    }
    // Show month/year format instead of date range
    return formatMonthYear(selectedMonth, selectedYear);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Build export parameters using the same logic as fetchDitDrls
      const exportParams = {
        page: 0,
        size: 200
      };

      // Collect statuses from both default and advanced filters
      const statuses = [];
      if (selectedStatus) {
        statuses.push(selectedStatus);
      }
      if (advancedFilters.statuses && advancedFilters.statuses.length > 0) {
        statuses.push(...advancedFilters.statuses);
      }
      // Remove duplicates
      if (statuses.length > 0) {
        exportParams.statuses = [...new Set(statuses)];
      }

      // Collect practice codes
      const practiceCodes = [];
      if (selectedPractice) {
        practiceCodes.push(selectedPractice);
      }
      if (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) {
        practiceCodes.push(...advancedFilters.practiceCodes);
      }
      // Remove duplicates
      if (practiceCodes.length > 0) {
        exportParams.practiceCodes = [...new Set(practiceCodes)];
      }

      // Location codes (array)
      if (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) {
        exportParams.locationCodes = [...new Set(advancedFilters.locationCodes)];
      }

      // Assignee IDs (array)
      if (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) {
        exportParams.assigneeIds = [...new Set(advancedFilters.assigneeIds)];
      }
      if (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) {
        exportParams.excludeAssigneeIds = [...new Set(advancedFilters.excludeAssigneeIds)];
      }

      // Reportee IDs (array)
      if (advancedFilters.reporteeIds && advancedFilters.reporteeIds.length > 0) {
        exportParams.reporteeIds = [...new Set(advancedFilters.reporteeIds)];
      }
      if (advancedFilters.excludeReporteeIds && advancedFilters.excludeReporteeIds.length > 0) {
        exportParams.excludeReporteeIds = [...new Set(advancedFilters.excludeReporteeIds)];
      }

      // Assignee Types (array) - Transform ONSHORE/OFFSHORE to ON-SHORE/OFF-SHORE for API
      if (advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0) {
        exportParams.assigneeTypes = [...new Set(advancedFilters.assigneeTypes)].map(type => {
          if (type === 'ONSHORE') return 'ON-SHORE';
          if (type === 'OFFSHORE') return 'OFF-SHORE';
          return type;
        });
      }

      // Reportee Types (array) - Transform ONSHORE/OFFSHORE to ON-SHORE/OFF-SHORE for API
      if (advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0) {
        exportParams.reporteeTypes = [...new Set(advancedFilters.reporteeTypes)].map(type => {
          if (type === 'ONSHORE') return 'ON-SHORE';
          if (type === 'OFFSHORE') return 'OFF-SHORE';
          return type;
        });
      }

      // DRL number (supports wildcards)
      if (advancedFilters.drlNumber && advancedFilters.drlNumber.trim() !== '') {
        exportParams.drlNumber = advancedFilters.drlNumber.trim();
      }

      // Batch number (supports wildcards)
      if (advancedFilters.batchNumber && advancedFilters.batchNumber.trim() !== '') {
        exportParams.batchNumber = advancedFilters.batchNumber.trim();
      }

      // Date filters - check if user has set any date filters
      const hasDrlReceivedDateFilter = advancedFilters.drlReceivedFrom || advancedFilters.drlReceivedTo;
      const hasReconciliationDateFilter = advancedFilters.reconciliationDateFrom || advancedFilters.reconciliationDateTo;
      const hasCompletedDateFilter = advancedFilters.completedDateFrom || advancedFilters.completedDateTo;
      const hasCreatedAtFilter = advancedFilters.createdAtFrom || advancedFilters.createdAtTo;
      
      // Check if any non-date advanced filters are active
      const hasNonDateAdvancedFilters = (
        (advancedFilters.statuses && advancedFilters.statuses.length > 0) ||
        (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) ||
        (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) ||
        (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) ||
        (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) ||
        (advancedFilters.reporteeIds && advancedFilters.reporteeIds.length > 0) ||
        (advancedFilters.excludeReporteeIds && advancedFilters.excludeReporteeIds.length > 0) ||
        (advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0) ||
        (advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0) ||
        (advancedFilters.reconciliationStatuses && advancedFilters.reconciliationStatuses.length > 0) ||
        (advancedFilters.drlNumber && advancedFilters.drlNumber.trim() !== '') ||
        (advancedFilters.batchNumber && advancedFilters.batchNumber.trim() !== '')
      );
      
      // Get month range for default date received
      const monthRange = getMonthRange(selectedMonth, selectedYear);
      
      // Only add date received filter if:
      // 1. User explicitly set DRL received date filters (use those instead of month range), OR
      // 2. Use default month range ONLY when no advanced filters are active
      if (hasDrlReceivedDateFilter) {
        // User has explicitly set a custom date range in advanced filters
        if (advancedFilters.drlReceivedFrom) {
          exportParams.drlReceivedFrom = advancedFilters.drlReceivedFrom;
        }
        if (advancedFilters.drlReceivedTo) {
          exportParams.drlReceivedTo = advancedFilters.drlReceivedTo;
        }
      } else if (!hasReconciliationDateFilter && !hasCompletedDateFilter && !hasCreatedAtFilter && !hasNonDateAdvancedFilters) {
        // No custom date filters or non-date filters set - use default month range
        exportParams.drlReceivedFrom = monthRange.startDate;
        exportParams.drlReceivedTo = monthRange.endDate;
      }
      
      // Reconciliation date filters
      if (advancedFilters.reconciliationDateFrom) {
        exportParams.reconciliationDateFrom = advancedFilters.reconciliationDateFrom;
      }
      if (advancedFilters.reconciliationDateTo) {
        exportParams.reconciliationDateTo = advancedFilters.reconciliationDateTo;
      }
      
      // Completed date filters
      if (advancedFilters.completedDateFrom) {
        exportParams.completedDateFrom = advancedFilters.completedDateFrom;
      }
      if (advancedFilters.completedDateTo) {
        exportParams.completedDateTo = advancedFilters.completedDateTo;
      }
      
      // Created at filters
      if (advancedFilters.createdAtFrom) {
        exportParams.createdAtFrom = advancedFilters.createdAtFrom;
      }
      if (advancedFilters.createdAtTo) {
        exportParams.createdAtTo = advancedFilters.createdAtTo;
      }
      
      // Call export API
      const blob = await api.exportDrlReport(exportParams);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with date
      const timestamp = new Date().toISOString().slice(0, 10);
      link.download = `drl-report-${timestamp}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('Error exporting DRL report:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to export DRL report';
      showError(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };


  const totals = ditDrls.reduce((acc, drl) => ({
    totalDrlAmount: acc.totalDrlAmount + (drl.totalDrlAmount || 0),
    totalPostedAmount: acc.totalPostedAmount + (drl.totalPostedAmount || 0),
    remainingAmount: acc.remainingAmount + (drl.remainingAmount || 0),
    checksAmount: acc.checksAmount + (drl.checksAmount || 0),
    onshorePostingAmount: acc.onshorePostingAmount + (drl.onshorePostingAmount || 0),
    offshorePostingAmount: acc.offshorePostingAmount + (drl.offshorePostingAmount || 0),
    otherAmount: acc.otherAmount + (drl.otherAmount || 0)
  }), { totalDrlAmount: 0, totalPostedAmount: 0, remainingAmount: 0, checksAmount: 0, onshorePostingAmount: 0, offshorePostingAmount: 0, otherAmount: 0 });

  const SortArrow = ({ field }) => {
    if (sortField !== field) return null;
    return (
      <span className="sort-arrow">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <div className="dit-drl-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">DRL</h1>
          <p className="page-subtitle">Manage and reconcile DRL records</p>
        </div>
        <div className="header-actions">
          {!hasAdvancedFilters() && (
            <div className="date-range-container">
              <button 
                className="date-range-btn"
                onClick={() => setShowMonthPicker(!showMonthPicker)}
              >
                {getDateRangeDisplay()}
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ marginLeft: '8px' }}>
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {showMonthPicker && (
                <MonthYearPicker
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  onSelect={handleMonthYearSelect}
                  onClose={() => setShowMonthPicker(false)}
                />
              )}
            </div>
          )}
          {hasAdvancedFilters() && (
            <div className="date-range-container">
              <div className="date-range-display">
                {getDateRangeDisplay()}
              </div>
            </div>
          )}
          <button 
            onClick={handleExport}
            disabled={isExporting}
            className="btn-export"
            title="Export to Excel"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
          <button 
            className="btn-primary" 
            onClick={() => navigate('/dit-drl/new')}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M10 3V17M3 10H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            New DRL
          </button>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-bar">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2"/>
            <path d="M15 15L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search by DRL number, amount..."
            value={searchTerm}
            onChange={(e) => {
              const filteredValue = filterEmojis(e.target.value);
              setSearchTerm(filteredValue);
            }}
          />
        </div>
        <div className="filter-dropdowns">
          <SearchableDropdown
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'NOT_STARTED', label: 'Not Started' },
              { value: 'IN_PROGRESS', label: 'In Progress' },
              { value: 'UNDER_CLARIFICATIONS', label: 'Under Clarifications' },
              { value: 'COMPLETED', label: 'Completed' },
              { value: 'OVER_POSTED', label: 'Over Posted' }
            ]}
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value)}
            placeholder="All Statuses"
            maxVisibleItems={5}
          />
          <SearchableDropdown
            options={[
              { value: '', label: 'All Practices' },
              ...practices.map(practice => ({
                value: practice.code || practice.practiceCode,
                label: practice.code || practice.practiceCode
              }))
            ]}
            value={selectedPractice}
            onChange={(value) => setSelectedPractice(value)}
            placeholder="All Practices"
            maxVisibleItems={5}
          />
          <button 
            className={`btn-advanced-filter ${hasAdvancedFilters() ? 'active' : ''}`}
            onClick={() => setShowAdvancedFilters(true)}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M3 5H17M7 10H17M11 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Advanced Filters
          </button>
        </div>
      </div>

      {/* Active Filters Indicator */}
      {(hasAdvancedFilters() || selectedStatus || selectedPractice || searchTerm) && (
        <div className="active-filters-section">
          <div className="active-filters-label">Active Filters:</div>
          <div className="active-filters-chips">
            {searchTerm && (
              <div className="filter-chip">
                <span>Search: {searchTerm}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => setSearchTerm('')}
                  title="Remove search filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {selectedStatus && (
              <div className="filter-chip">
                <span>Status: {[
                  { value: 'NOT_STARTED', label: 'Not Started' },
                  { value: 'IN_PROGRESS', label: 'In Progress' },
                  { value: 'UNDER_CLARIFICATIONS', label: 'Under Clarifications' },
                  { value: 'COMPLETED', label: 'Completed' },
                  { value: 'OVER_POSTED', label: 'Over Posted' }
                ].find(s => s.value === selectedStatus)?.label || selectedStatus}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => setSelectedStatus('')}
                  title="Remove status filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {selectedPractice && (
              <div className="filter-chip">
                <span>Practice: {selectedPractice}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => setSelectedPractice('')}
                  title="Remove practice filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {/* Multiple Status Filters */}
            {advancedFilters.statuses && advancedFilters.statuses.length > 0 && advancedFilters.statuses.map((status) => {
              const statusLabels = {
                'NOT_STARTED': 'Not Started',
                'IN_PROGRESS': 'In Progress',
                'UNDER_CLARIFICATIONS': 'Under Clarifications',
                'COMPLETED': 'Completed',
                'OVER_POSTED': 'Over Posted'
              };
              return (
                <div key={status} className="filter-chip">
                  <span>Status: {statusLabels[status] || status}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        statuses: prev.statuses.filter(s => s !== status)
                      }));
                    }}
                    title="Remove status filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {/* Multiple Practice Code Filters */}
            {advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0 && advancedFilters.practiceCodes.map((practiceCode) => (
              <div key={practiceCode} className="filter-chip">
                <span>Practice: {practiceCode}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      practiceCodes: prev.practiceCodes.filter(s => s !== practiceCode)
                    }));
                  }}
                  title="Remove practice code filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {/* Multiple Location Code Filters */}
            {advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0 && advancedFilters.locationCodes.map((locationCode) => (
              <div key={locationCode} className="filter-chip">
                <span>Location: {locationCode}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      locationCodes: prev.locationCodes.filter(s => s !== locationCode)
                    }));
                  }}
                  title="Remove location code filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {/* Multiple Assignee Filters */}
            {advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0 && advancedFilters.assigneeIds.map((assigneeId) => (
              <div key={assigneeId} className="filter-chip">
                <span>Assignee: {getUserName(assigneeId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      assigneeIds: prev.assigneeIds.filter(id => id !== assigneeId)
                    }));
                  }}
                  title="Remove assignee filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0 && advancedFilters.excludeAssigneeIds.map((assigneeId) => (
              <div key={`exclude-assignee-${assigneeId}`} className="filter-chip filter-chip--exclude">
                <span>Assignee: Not {getUserName(assigneeId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      excludeAssigneeIds: prev.excludeAssigneeIds.filter(id => id !== assigneeId)
                    }));
                  }}
                  title="Remove excluded assignee filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {/* Multiple Reportee Filters */}
            {advancedFilters.reporteeIds && advancedFilters.reporteeIds.length > 0 && advancedFilters.reporteeIds.map((reporteeId) => (
              <div key={reporteeId} className="filter-chip">
                <span>Reporter: {getUserName(reporteeId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      reporteeIds: prev.reporteeIds.filter(id => id !== reporteeId)
                    }));
                  }}
                  title="Remove reporter filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludeReporteeIds && advancedFilters.excludeReporteeIds.length > 0 && advancedFilters.excludeReporteeIds.map((reporteeId) => (
              <div key={`exclude-reportee-${reporteeId}`} className="filter-chip filter-chip--exclude">
                <span>Reporter: Not {getUserName(reporteeId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      excludeReporteeIds: prev.excludeReporteeIds.filter(id => id !== reporteeId)
                    }));
                  }}
                  title="Remove excluded reporter filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {/* Multiple Assignee Type Filters */}
            {advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0 && advancedFilters.assigneeTypes.map((assigneeType) => {
              const assigneeTypeLabels = {
                'ONSHORE': 'Onshore',
                'OFFSHORE': 'Offshore'
              };
              return (
                <div key={assigneeType} className="filter-chip">
                  <span>Assignee: {assigneeTypeLabels[assigneeType] || assigneeType}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        assigneeTypes: prev.assigneeTypes.filter(at => at !== assigneeType)
                      }));
                    }}
                    title="Remove assignee type filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {/* Multiple Reportee Type Filters */}
            {advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0 && advancedFilters.reporteeTypes.map((reporteeType) => {
              const reporteeTypeLabels = {
                'ONSHORE': 'Onshore',
                'OFFSHORE': 'Offshore'
              };
              return (
                <div key={reporteeType} className="filter-chip">
                  <span>Reporter: {reporteeTypeLabels[reporteeType] || reporteeType}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        reporteeTypes: prev.reporteeTypes.filter(rt => rt !== reporteeType)
                      }));
                    }}
                    title="Remove reporter type filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {/* Multiple Reconciliation Status Filters */}
            {advancedFilters.reconciliationStatuses && advancedFilters.reconciliationStatuses.length > 0 && advancedFilters.reconciliationStatuses.map((status) => {
              const statusLabels = {
                'RECONCILED': 'Reconciled',
                'OVER_RECONCILED': 'Excess Reconciled',
                'UNRECONCILED': 'Pending Reconciliation'
              };
              return (
                <div key={status} className="filter-chip">
                  <span>Reconciliation: {statusLabels[status] || status}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        reconciliationStatuses: prev.reconciliationStatuses.filter(s => s !== status)
                      }));
                    }}
                    title="Remove reconciliation status filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {advancedFilters.drlNumber && (
              <div className="filter-chip">
                <span>DRL Number: {advancedFilters.drlNumber}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, drlNumber: '' }));
                  }}
                  title="Remove DRL number filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {advancedFilters.batchNumber && (
              <div className="filter-chip">
                <span>Batch Number: {advancedFilters.batchNumber}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, batchNumber: '' }));
                  }}
                  title="Remove batch number filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.drlReceivedFrom || advancedFilters.drlReceivedTo) && (
              <div className="filter-chip">
                <span>DRL Received: {advancedFilters.drlReceivedFrom && advancedFilters.drlReceivedTo 
                  ? formatDateRange(advancedFilters.drlReceivedFrom, advancedFilters.drlReceivedTo)
                  : (advancedFilters.drlReceivedFrom ? formatDateUS(advancedFilters.drlReceivedFrom) : formatDateUS(advancedFilters.drlReceivedTo))}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, drlReceivedFrom: '', drlReceivedTo: '' }));
                  }}
                  title="Remove DRL received date filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.createdAtFrom || advancedFilters.createdAtTo) && (
              <div className="filter-chip">
                <span>DRL Created: {advancedFilters.createdAtFrom && advancedFilters.createdAtTo 
                  ? formatDateRange(advancedFilters.createdAtFrom, advancedFilters.createdAtTo)
                  : (advancedFilters.createdAtFrom ? formatDateUS(advancedFilters.createdAtFrom) : formatDateUS(advancedFilters.createdAtTo))}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, createdAtFrom: '', createdAtTo: '' }));
                  }}
                  title="Remove DRL created date filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.completedDateFrom || advancedFilters.completedDateTo) && (
              <div className="filter-chip">
                <span>Posting Completed: {advancedFilters.completedDateFrom && advancedFilters.completedDateTo 
                  ? formatDateRange(advancedFilters.completedDateFrom, advancedFilters.completedDateTo)
                  : (advancedFilters.completedDateFrom ? formatDateUS(advancedFilters.completedDateFrom) : formatDateUS(advancedFilters.completedDateTo))}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, completedDateFrom: '', completedDateTo: '' }));
                  }}
                  title="Remove posting completed date filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.reconciliationDateFrom || advancedFilters.reconciliationDateTo) && (
              <div className="filter-chip">
                <span>Reconciliation: {advancedFilters.reconciliationDateFrom && advancedFilters.reconciliationDateTo 
                  ? formatDateRange(advancedFilters.reconciliationDateFrom, advancedFilters.reconciliationDateTo)
                  : (advancedFilters.reconciliationDateFrom ? formatDateUS(advancedFilters.reconciliationDateFrom) : formatDateUS(advancedFilters.reconciliationDateTo))}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, reconciliationDateFrom: '', reconciliationDateTo: '' }));
                  }}
                  title="Remove reconciliation date filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.month || advancedFilters.year) && (
              <div className="filter-chip">
                <span>Period: {formatMonthYear(advancedFilters.month || selectedMonth, advancedFilters.year || selectedYear)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, month: null, year: null }));
                  }}
                  title="Remove period filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            <button
              className="clear-all-filters-btn"
              onClick={handleClearAllFilters}
              title="Clear all filters"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="table-section">
        <div className="table-header">
          <div className="table-header-left">
            {totalElements > 0 && showPagination && (
              <div className="pagination-info">
                <button
                  className="pagination-arrow"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 0 || loading}
                  title="Previous page"
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                    <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <span className="pagination-text">
                  {startIndex} - {endIndex}, of {displayTotal}
                </span>
                {totalPages > 1 && (
                  <div className="pagination-page-input">
                    <span style={{ fontSize: '12px', color: '#6b7280', marginRight: '4px' }}>Page:</span>
                    <input
                      type="text"
                      value={pageInputValue}
                      onChange={handlePageInputChange}
                      onKeyPress={handlePageInputKeyPress}
                      onBlur={handleGoToPage}
                      style={{
                        width: '45px',
                        padding: '2px 4px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        fontSize: '12px',
                        textAlign: 'center',
                        height: '24px'
                      }}
                    />
                    <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>
                      of {totalPages}
                    </span>
                  </div>
                )}
                <button
                  className="pagination-arrow"
                  onClick={handleNextPage}
                  disabled={!hasMore || loading}
                  title="Next page"
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="table-actions">
            <Tooltip text="Reset Column widths" position="top">
              <button
                type="button"
                className="icon-btn"
                onClick={resetColumnWidths}
                aria-label="Reset Column widths"
              >
                <RotateCcw size={18} strokeWidth={2} />
              </button>
            </Tooltip>
            <button 
              ref={columnSelectorTriggerRef}
              className="icon-btn"
              onClick={() => setShowColumnSelector(!showColumnSelector)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 5H17M3 10H17M3 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="dit-drl-table dit-drl-table--col-resize">
            <Colgroup />
            <thead>
              <tr className="totals-row">
                {visibleColumns.map((colKey, index) => {
                  // Show "Totals:" in the first column
                  if (index === 0) {
                    return (
                      <th key={colKey}>
                        <Tooltip text="Totals reflect only the rows currently displayed on this page" position="top">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <Info 
                              size={16} 
                              style={{ 
                                color: '#374151', 
                                cursor: 'pointer',
                                transition: 'color 0.2s',
                                fontWeight: 'bold',
                                strokeWidth: 2.5,
                                flexShrink: 0
                              }}
                              onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                              onMouseLeave={(e) => e.target.style.color = '#374151'}
                            />
                            Totals:
                          </span>
                        </Tooltip>
                      </th>
                    );
                  }
                  
                  // Show totals for columns that have them
                  switch (colKey) {
                    case 'totalDrlAmount':
                      return <th key={colKey}>{formatCurrency(totals.totalDrlAmount)}</th>;
                    case 'totalPostedAmount':
                      return <th key={colKey}>{formatCurrency(totals.totalPostedAmount)}</th>;
                    case 'remainingAmount':
                      return <th key={colKey}>{formatCurrency(totals.remainingAmount)}</th>;
                    case 'checksAmount':
                      return <th key={colKey}>{formatCurrency(totals.checksAmount)}</th>;
                    case 'onshorePostingAmount':
                      return <th key={colKey}>{formatCurrency(totals.onshorePostingAmount)}</th>;
                    case 'offshorePostingAmount':
                      return <th key={colKey}>{formatCurrency(totals.offshorePostingAmount)}</th>;
                    case 'otherAmount':
                      return <th key={colKey}>{formatCurrency(totals.otherAmount)}</th>;
                    case 'action':
                      return <th key={colKey}></th>;
                    default:
                      return <th key={colKey}></th>;
                  }
                })}
              </tr>
              <tr>
                {visibleColumns.map(colKey => {
                  const column = allColumns.find(col => col.key === colKey);
                  if (!column) return null;

                  switch (colKey) {
                    case 'drlReceivedDate':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('drlReceivedDate')}
                        >
                          Received date <SortArrow field="drlReceivedDate" />
                        </ResizableTh>
                      );
                    case 'drlNumber':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('drlNumber')}
                        >
                          DRL number <SortArrow field="drlNumber" />
                        </ResizableTh>
                      );
                    case 'totalDrlAmount':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('totalDrlAmount')}
                        >
                          Total drl $ <SortArrow field="totalDrlAmount" />
                        </ResizableTh>
                      );
                    case 'totalPostedAmount':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('totalPostedAmount')}
                        >
                          Total posted $ <SortArrow field="totalPostedAmount" />
                        </ResizableTh>
                      );
                    case 'remainingAmount':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('remainingAmount')}
                        >
                          Remaining $ <SortArrow field="remainingAmount" />
                        </ResizableTh>
                      );
                    case 'checksAmount':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('checksAmount')}
                        >
                          Total received in bank $ <SortArrow field="checksAmount" />
                        </ResizableTh>
                      );
                    case 'reconciliationStatus':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('reconciliationStatus')}
                        >
                          Reconciliation status <SortArrow field="reconciliationStatus" />
                        </ResizableTh>
                      );
                    case 'status':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Status
                        </ResizableTh>
                      );
                    case 'action':
                      return isSuperAdmin ? (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Action
                        </ResizableTh>
                      ) : null;
                    case 'updatedAt':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('updatedAt')}
                        >
                          Updated at <SortArrow field="updatedAt" />
                        </ResizableTh>
                      );
                    case 'practiceCode':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('practiceCode')}
                        >
                          Practice code <SortArrow field="practiceCode" />
                        </ResizableTh>
                      );
                    case 'locationCode':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('locationCode')}
                        >
                          Location code <SortArrow field="locationCode" />
                        </ResizableTh>
                      );
                    case 'createdAt':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('createdAt')}
                        >
                          Created at <SortArrow field="createdAt" />
                        </ResizableTh>
                      );
                    case 'reconciliationDate':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('reconciliationDate')}
                        >
                          Reconciliation date <SortArrow field="reconciliationDate" />
                        </ResizableTh>
                      );
                    case 'completedDate':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('completedDate')}
                        >
                          Posting completed date <SortArrow field="completedDate" />
                        </ResizableTh>
                      );
                    case 'offshorePostingAmount':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('offshorePostingAmount')}
                        >
                          offShore Posting <SortArrow field="offshorePostingAmount" />
                        </ResizableTh>
                      );
                    case 'otherAmount':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('otherAmount')}
                        >
                          Other Amount <SortArrow field="otherAmount" />
                        </ResizableTh>
                      );
                    case 'onshorePostingAmount':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('onshorePostingAmount')}
                        >
                          onShore Posting <SortArrow field="onshorePostingAmount" />
                        </ResizableTh>
                      );
                    case 'assigneeName':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('assigneeName')}
                        >
                          Assignee Name <SortArrow field="assigneeName" />
                        </ResizableTh>
                      );
                    case 'reporterName':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('reporterName')}
                        >
                          Reporter Name <SortArrow field="reporterName" />
                        </ResizableTh>
                      );
                    default:
                      return null;
                  }
                })}
              </tr>
            </thead>
            <tbody>
              {loading && ditDrls.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length} style={{ textAlign: 'center', padding: '40px' }}>
                    Loading DRLs...
                  </td>
                </tr>
              ) : ditDrls.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length} style={{ textAlign: 'center', padding: '40px' }}>
                    No DRLs found
                  </td>
                </tr>
              ) : (
                ditDrls.map((drl) => (
                  <tr
                    key={drl.drlId}
                    className="dit-drl-table-row--clickable"
                    onClick={() => goToDrlDetail(drl)}
                  >
                    {visibleColumns.map(colKey => {
                      switch (colKey) {
                        case 'drlReceivedDate':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={drl.drlReceivedDate ? formatDateUS(drl.drlReceivedDate) : ''} />
                            </td>
                          );
                        case 'drlNumber':
                          return (
                            <td key={colKey} className="drl-number-table-cell">
                              {drl.drlNumber ? (
                                <div className="drl-number-cell-inner">
                                  <button
                                    type="button"
                                    className="dit-drl-copy-icon-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopyDrlNumber(drl.drlNumber);
                                    }}
                                    title="Copy DRL number"
                                    aria-label="Copy DRL number"
                                  >
                                    <Copy size={16} aria-hidden />
                                  </button>
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <CellEllipsis text={drl.drlNumber} />
                                  </div>
                                </div>
                              ) : (
                                <CellEllipsis text="" />
                              )}
                            </td>
                          );
                        case 'totalDrlAmount':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={formatCurrency(drl.totalDrlAmount)} />
                            </td>
                          );
                        case 'totalPostedAmount':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={formatCurrency(drl.totalPostedAmount)} />
                            </td>
                          );
                        case 'remainingAmount':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={formatCurrency(drl.remainingAmount)} />
                            </td>
                          );
                        case 'checksAmount':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={formatCurrency(drl.checksAmount)} />
                            </td>
                          );
                        case 'reconciliationStatus':
                          return (
                            <td key={colKey}>
                              <span className={`status-badge ${getReconciliationStatusClass(drl.reconciliationStatus)}`}>
                                <CellEllipsis text={formatReconciliationStatus(drl.reconciliationStatus)} />
                              </span>
                            </td>
                          );
                        case 'status':
                          return (
                            <td key={colKey}>
                              <span className={`status-badge ${getStatusClass(drl.status)}`}>
                                <CellEllipsis text={formatStatus(drl.status)} />
                              </span>
                            </td>
                          );
                        case 'updatedAt':
                          return (
                            <td key={colKey} className="td-timestamp-cell">
                              <UserTimestamp
                                userId={drl.updatedBy}
                                dateTime={drl.updatedAt}
                                action="updated"
                                getUserName={getUserName}
                                getUserById={getUserById}
                              />
                            </td>
                          );
                        case 'practiceCode':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={drl.practiceCode || ''} />
                            </td>
                          );
                        case 'locationCode':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={drl.locationCode || ''} />
                            </td>
                          );
                        case 'createdAt':
                          return (
                            <td key={colKey} className="td-timestamp-cell">
                              <UserTimestamp
                                userId={drl.createdBy}
                                dateTime={drl.createdAt}
                                action="created"
                                getUserName={getUserName}
                                getUserById={getUserById}
                              />
                            </td>
                          );
                        case 'reconciliationDate':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={drl.reconciliationDate ? formatDateUS(drl.reconciliationDate) : ''} />
                            </td>
                          );
                        case 'completedDate':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={drl.completedDate ? formatDateUS(drl.completedDate) : ''} />
                            </td>
                          );
                        case 'offshorePostingAmount':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={formatCurrency(drl.offshorePostingAmount)} />
                            </td>
                          );
                        case 'otherAmount':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={formatCurrency(drl.otherAmount)} />
                            </td>
                          );
                        case 'onshorePostingAmount':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={formatCurrency(drl.onshorePostingAmount)} />
                            </td>
                          );
                        case 'assigneeName':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={drl.assigneeName || 'N/A'} />
                            </td>
                          );
                        case 'reporterName':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={drl.reporterName || 'N/A'} />
                            </td>
                          );
                        case 'action':
                          return isSuperAdmin ? (
                            <td key={colKey} onClick={(e) => e.stopPropagation()}>
                              <button 
                                className="btn-icon delete"
                                onClick={() => handleDeleteClick(drl)}
                                title="Delete"
                                style={{ color: '#dc2626' }}
                              >
                                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                  <path d="M3 6H5H17M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2C10.5304 2 11.0391 2.21071 11.4142 2.58579C11.7893 2.96086 12 3.46957 12 4V6M15 6V16C15 16.5304 14.7893 17.0391 14.4142 17.4142C14.0391 17.7893 13.5304 18 13 18H7C6.46957 18 5.96086 17.7893 5.58579 17.4142C5.21071 17.0391 5 16.5304 5 16V6H15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            </td>
                          ) : null;
                        default:
                          return null;
                      }
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>

      <AdvancedFilterDrawer
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        filters={advancedFilters}
        onApplyFilters={handleApplyAdvancedFilters}
        onResetFilters={handleResetAdvancedFilters}
        isDitDrl={true}
      />

      {showColumnSelector && (
        <ColumnSelector
          availableColumns={allColumns}
          visibleColumns={visibleColumns}
          defaultColumns={defaultColumns}
          onChange={(cols) => {
            setVisibleColumns(cols);
            persistVisibleColumns(DITDRL_COLUMN_PREFS_KEY, cols);
          }}
          onClose={() => setShowColumnSelector(false)}
          triggerRef={columnSelectorTriggerRef}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div 
          className="modal-overlay"
          onClick={handleDeleteCancel}
        >
          <div 
            className="modal-content delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="modal-message">
              Are you sure you want to delete DRL <strong>"{drlToDelete?.drlNumber || 'Unknown'}"</strong>? This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button 
                className="btn-cancel" 
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="btn-save" 
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DitDrl;
